import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const FAQPage: React.FC = () => {
  const faqs = useAdminStore((s) => s.faqs);
  const { t, localize } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const published = [...(faqs ?? [])]
    .filter((f) => f.isPublished)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const toggle = (id: string): void => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Header */}
      <div className="pt-32 sm:pt-40 pb-16 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-syncopate font-bold tracking-tighter text-black dark:text-white">
            FAQ
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="mt-4 text-[10px] sm:text-xs font-bold uppercase tracking-[0.4em] text-black dark:text-white"
          >
            {t.nav.home === 'Home' ? 'Frequently Asked Questions' : 'ხშირად დასმული კითხვები'}
          </motion.p>
        </motion.div>
      </div>

      {/* FAQ List */}
      <div className="max-w-3xl mx-auto px-6 pb-32">
        {published.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
              {t.nav.home === 'Home' ? 'No questions yet' : 'კითხვები ჯერ არ არის'}
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {published.map((faq, index) => {
              const isOpen = openId === faq._id;
              const question = localize(faq.question);
              const answer = localize(faq.answer);

              return (
                <motion.div
                  key={faq._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-black/[0.06] dark:border-white/[0.06]"
                >
                  <button
                    onClick={() => toggle(faq._id)}
                    className="w-full flex items-center justify-between gap-6 py-6 sm:py-8 text-left group"
                  >
                    <div className="flex items-start gap-4 sm:gap-6 min-w-0">
                      <span className="text-[10px] font-bold text-black/15 dark:text-white/15 mt-1.5 shrink-0 font-mono tabular-nums">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-base sm:text-lg font-bold text-black dark:text-white group-hover:text-black/70 dark:group-hover:text-white/70 transition-colors leading-snug">
                        {question}
                      </h3>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="w-10 h-10 rounded-full border border-black/[0.08] dark:border-white/[0.08] flex items-center justify-center shrink-0 group-hover:border-black/20 dark:group-hover:border-white/20 transition-colors"
                    >
                      {isOpen ? (
                        <Minus size={16} className="text-black dark:text-white" />
                      ) : (
                        <Plus size={16} className="text-black/40 dark:text-white/40" />
                      )}
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-10 sm:pl-14 pr-16 pb-8">
                          <p className="text-sm sm:text-[15px] leading-relaxed text-black/50 dark:text-white/50 whitespace-pre-line">
                            {answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQPage;
